import { chartColor, chartColorPinned } from '@/shared/lib/chart-theme';
import {
  BarElement,
  CategoryScale,
  Chart,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import { merge } from 'chart.js/helpers';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title
);

const DualBarBackgroundPlugin: Plugin<'bar'> = {
  id: 'barBackground',
  beforeDraw: (chart: Chart<'bar'>) => {
    const { ctx, scales } = chart;
    const isHorizontal = chart.options.indexAxis === 'y';

    chart.data.datasets.forEach((_, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.type !== 'bar') return;

      meta.data.forEach((bar) => {
        const barElement = bar as BarElement;

        ctx.save();

        if (isHorizontal) {
          // For horizontal bars
          const { x } = scales;
          const { y: barElementY, height: barElementHeight } =
            barElement.getProps(['y', 'height'], true);

          if (barElementY && barElementHeight) {
            const yPos = barElementY - barElementHeight / 2;
            const height = barElementHeight;
            const xLeft = x.left;
            const xWidth = x.width;

            ctx.fillStyle = chartColor('--color-chart-track');
            ctx.fillRect(xLeft, yPos, xWidth, height);
          }
        } else {
          // For vertical bars
          const { y } = scales;
          const { x: barElementX, width: barElementWidth } =
            barElement.getProps(['x', 'width'], true);

          if (barElementX && barElementWidth) {
            const xPos = barElementX - barElementWidth / 2;
            const width = barElementWidth;
            const yZero = y.getPixelForValue(0);
            const yMax = y.getPixelForValue(y.max);

            ctx.fillStyle = chartColorPinned(
              '--color-chart-track',
              '#D6DBED66'
            );
            ctx.fillRect(xPos, yMax, width, yZero - yMax);
          }
        }

        ctx.restore();
      });
    });
  },
};

type DualBarChartChartProps = {
  data: ChartData<'bar'>;
  options?: ChartOptions<'bar'>;
};

export const DualBarChart = ({ data, options }: DualBarChartChartProps) => {
  const defaultOptions: ChartOptions<'bar'> = {
    indexAxis: options?.indexAxis ?? 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        displayColors: false,
        backgroundColor: chartColor('--color-neutral-1'),
        borderColor: chartColorPinned('--color-neutral-5', '#e1e1e1'),
        borderWidth: 1,
        bodyColor: chartColor('--color-neutral-8'),
        titleColor: chartColor('--color-neutral-9'),
        padding: 8,
        cornerRadius: 2,
        filter: (tooltipItem) => {
          const bg = tooltipItem.dataset.backgroundColor;
          const color = Array.isArray(bg) ? bg[tooltipItem.dataIndex] : bg;
          return color !== '#E8EDF5';
        },
        callbacks: {
          title: options?.plugins?.tooltip?.callbacks?.title,
          label: options?.plugins?.tooltip?.callbacks?.label,
        },
      },
    },
    scales: {
      x: {
        position: options?.scales?.x?.position ?? 'top',
        beginAtZero: true,
        stacked: true,
        ticks: {
          stepSize: 10,
          maxRotation: 0,
        },
        grid: {
          drawTicks: false,
        },
        border: {
          color: '#00001A4D',
          width: options?.scales?.x?.border?.width ?? 0,
          dash: [2, 3],
        },
      },
      y: {
        stacked: true,
        grid: {
          drawTicks: false,
        },
        border: {
          color: '#00001A4D',
          width: options?.scales?.y?.border?.width ?? 1.5,
          dash: [2, 3],
        },
        ticks: {
          stepSize:
            options?.scales?.y?.ticks && 'stepSize' in options.scales.y.ticks
              ? options.scales.y.ticks.stepSize
              : 10,
          maxRotation: 0,
        },
        max: options?.scales?.y?.max,
      },
    },
  };

  const mergedOptions = merge(defaultOptions, options);

  return (
    <Bar
      className="h-full"
      data={data}
      options={mergedOptions}
      plugins={[DualBarBackgroundPlugin]}
    />
  );
};
